if (!customElements.get('aplovio-gallery')) {
  class AplovioGallery extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.track = this.querySelector('.ag-track');
      this.items = [...this.querySelectorAll('.ag-item')];
      if (!this.track || !this.items.length) return;
      this.connected = true;
      this.scrollOwner = this.closest('[data-ap-scroll-owner]') || window;
      this.snapOwner = this.scrollOwner === window ? document.documentElement : this.scrollOwner;
      this.active = 0;
      this.abort = new AbortController();
      this.indicator = this.querySelector('aplovio-indicators');
      this.indicator?.addEventListener('aplovio:indicator-select', event => this.go(event.detail.index), {signal:this.abort.signal});
      const reveal = event => {if(event.target.matches?.('.ag-image'))event.target.setAttribute('data-loaded','');};
      this.addEventListener('load',reveal,{capture:true,signal:this.abort.signal});
      this.addEventListener('error',reveal,{capture:true,signal:this.abort.signal});
      this.querySelectorAll('.ag-image').forEach(image=>{if(image.complete)image.setAttribute('data-loaded','');});
      this.viewport = matchMedia('(min-width: 990px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.viewport.addEventListener('change', () => this.configure(true), {signal:this.abort.signal});
      this.addEventListener('click', event => this.handleClick(event), {signal:this.abort.signal});
      this.addEventListener('keydown', event => this.handleKey(event), {signal:this.abort.signal});
      this.addEventListener('aplovio:information-target',()=>this.bindInformation(),{signal:this.abort.signal});
      this.addEventListener('aplovio:media-select', event => {
        const index = this.items.findIndex(item => item.dataset.mediaId === String(event.detail?.id));
        if (index >= 0) this.go(index, true);
      }, {signal:this.abort.signal});
      this.zoom = this.querySelector('.ag-zoom');
      this.zoom?.addEventListener('close', () => this.finishZoom(), {signal:this.abort.signal});
      this.configure(false);
      const initial = this.items.findIndex(item => item.dataset.mediaId === this.dataset.initialMedia);
      if (initial > 0) requestAnimationFrame(() => this.isConnected && this.go(initial, false));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.modeAbort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.playObserver?.disconnect();
      this.preloadObserver?.disconnect();
      this.releasePageSnap();
      cancelAnimationFrame(this.frame);
      if (this.zoom) window.AplovioLayers?.dispose(this.zoom);
      if (this.zoom?.open) this.zoom.close();
      this.finishZoom();
      this.stopPlayers();
      this.connected = false;
    }
    configure(preserve) {
      const old = this.active;
      this.modeAbort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.releasePageSnap();
      this.preloadObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.modeAbort = new AbortController();
      this.mode = this.viewport.matches ? this.dataset.desktop : this.dataset.mobile;
      this.horizontal = ['thumbnails','slider','peek'].includes(this.mode);
      this.vertical = ['fullscreen', 'framed'].includes(this.mode);
      if (this.indicator) {
        this.indicator.dataset.axis = this.vertical ? 'vertical' : 'horizontal';
        if (!this.viewport.matches && ['dots', 'dashes', 'progress'].includes(this.dataset.indicators) && !this.indicatorLoading) {
          this.indicatorLoading = window.AplovioLazy.component(this.indicator).catch(() => {
            this.setAttribute('data-indicator-error', '');
            console.warn('[Aplovio] Gallery indicators unavailable; the image counter and gestures remain usable.');
          });
        }
      }
      // Observe actual visible media, including both columns and the peek.
      // Promote only those images and one ahead; keep responsive srcset selection.
      this.preloadObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const index = this.items.indexOf(entry.target);
          this.warmImage(index);
          this.warmImage(index + 1);
        });
      }, {threshold:0.01});
      this.items.forEach(item => this.preloadObserver.observe(item));
      this.removeAttribute('data-in-view');
      if (this.viewport.matches && this.mode === 'thumbnails') {
        const thumbnailSize = getComputedStyle(this).getPropertyValue('--ag-thumb-size').trim();
        this.querySelectorAll('[data-thumbnail-template]').forEach(template => {
          if (!template.parentElement.querySelector('img')) {
            const content = template.content.cloneNode(true);
            const image = content.querySelector('img');
            if (image) image.sizes = thumbnailSize;
            template.parentElement.append(content);
          }
        });
      } else this.querySelectorAll('.ag-thumbnail img').forEach(image => image.remove());
      if (this.horizontal) {
        this.track.addEventListener('scroll', () => this.schedule(() => this.syncHorizontal()), {passive:true,signal:this.modeAbort.signal});
        this.resizeObserver = new ResizeObserver(() => this.schedule(() => this.alignHorizontal(this.active, 'instant')));
        this.resizeObserver.observe(this.track);
      } else if (this.vertical) {
        this.snapOwner.style.setProperty('--ag-snap-exit-inset', this.scrollOwner === window ? 'var(--ap-header-height,0px)' : '0px');
        this.bindInformation();
        const ratios = new Map();
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => ratios.set(entry.target, entry.intersectionRatio));
          let best = this.active, score = 0;
          this.items.forEach((item,index) => {const ratio=ratios.get(item)||0;if(ratio>score){score=ratio;best=index;}});
          if (score > 0) this.setActive(best);
        }, {root:this.scrollOwner === window ? null : this.scrollOwner, threshold:[0,.15,.3,.5,.7,.9,1]});
        this.items.forEach(item => this.observer.observe(item));
        this.scrollOwner.addEventListener('scroll', () => this.schedule(() => this.syncFullscreen()), {passive:true,signal:this.modeAbort.signal});
        window.addEventListener('resize', () => this.schedule(() => this.syncFullscreen()), {passive:true,signal:this.modeAbort.signal});
        this.syncFullscreen();
      }
      this.setActive(old);
      if (preserve && this.horizontal) requestAnimationFrame(() => this.isConnected && this.alignHorizontal(old,'instant'));
      this.setAttribute('data-ready','');
    }
    schedule(callback) {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.isConnected && callback());
    }
    syncHorizontal() {
      const bounds=this.track.getBoundingClientRect(), rtl=getComputedStyle(this.track).direction==='rtl';
      let nearest=0, distance=Infinity;
      this.items.forEach((item,index) => {const r=item.getBoundingClientRect();const delta=Math.abs(rtl?r.right-bounds.right:r.left-bounds.left);if(delta<distance){nearest=index;distance=delta;}});
      this.setActive(nearest);
    }
    bindInformation() {
      this.information?.removeAttribute('data-gallery-snap-exit');
      this.information = this.closest('[data-ap-product]')?.querySelector(`#${CSS.escape(this.dataset.information)}`) || document.getElementById(this.dataset.information);
      if(this.vertical)this.information?.setAttribute('data-gallery-snap-exit','');
    }
    syncFullscreen() {
      const bounds=this.scrollOwner === window ? {top:0,height:window.innerHeight} : this.scrollOwner.getBoundingClientRect();
      const r=this.getBoundingClientRect(), mid=bounds.top+bounds.height*.5;
      // Release the page as soon as the information panel reaches the top.
      // Native snap-stop handles each swipe; no wheel/touch interception.
      const atEnd=(this.scrollOwner === window ? window.scrollY : this.scrollOwner.scrollTop)+bounds.height>=this.snapOwner.scrollHeight-2;
      const snapInset = (parseFloat(getComputedStyle(this.snapOwner).scrollPaddingTop) || 0) + (this.information ? parseFloat(getComputedStyle(this.information).scrollMarginTop) || 0 : 0);
      const snapping=this.dataset.scrollMode !== 'free' && !atEnd && r.top<bounds.top+bounds.height && (this.information?.getBoundingClientRect().top ?? r.bottom)>bounds.top+snapInset+2;
      // Finish geometry reads before changing either presentation state.
      this.toggleAttribute('data-in-view',r.top<mid && r.bottom>mid);
      this.snapOwner.classList.toggle('ag-page-snap',snapping);
    }
    releasePageSnap() {
      this.snapOwner?.classList.remove('ag-page-snap');
      this.snapOwner?.style.removeProperty('--ag-snap-exit-inset');
      this.information?.removeAttribute('data-gallery-snap-exit');
    }
    alignHorizontal(index,behavior) {
      const item=this.items[index];if(!item)return;
      const a=item.getBoundingClientRect(), b=this.track.getBoundingClientRect();
      const delta=getComputedStyle(this.track).direction==='rtl'?a.right-b.right:a.left-b.left;
      if(Math.abs(delta)>1)this.track.scrollBy({left:delta,behavior});
    }
    setActive(index) {
      const previous=this.active;
      this.active=Math.max(0,Math.min(index,this.items.length-1));
      if(previous!==this.active)this.items[previous]?.removeAttribute('data-active');
      if(!this.items[this.active].hasAttribute('data-active'))this.items[this.active].setAttribute('data-active','');
      if (this.indicator) {
        this.indicator.dataset.count = String(this.items.length);
        this.indicator.dataset.index = String(this.active);
      }
      if (this.horizontal || this.vertical) {
        this.warmImage(this.active);
        this.warmImage(this.active + 1);
      }
      this.querySelectorAll('[data-go]').forEach(control => {if(Number(control.dataset.go)===this.active)control.setAttribute('aria-current','true');else control.removeAttribute('aria-current');});
      this.querySelectorAll('[data-current]').forEach(counter=>counter.textContent=this.active+1);
      this.querySelectorAll('[data-previous]').forEach(button=>button.disabled=this.active===0);
      this.querySelectorAll('[data-next]').forEach(button=>button.disabled=this.active===this.items.length-1);
      this.stopPlayers(this.items[this.active]);
    }
    warmImage(index) {
      const image = this.items[index]?.querySelector('.ag-image');
      if (image && image.loading !== 'eager') image.loading = 'eager';
    }
    go(index,animate=true) {
      index=Math.max(0,Math.min(index,this.items.length-1));
      const behavior=animate&&!this.motion.matches?'smooth':'instant';
      this.setActive(index);
      if(this.horizontal)this.alignHorizontal(index,behavior);
      else if (animate && this.vertical) {
        const viewportTop = this.scrollOwner === window ? 0 : this.scrollOwner.getBoundingClientRect().top;
        const headerInset = parseFloat(getComputedStyle(this).getPropertyValue('--ag-header-height')) || 0;
        // Explicit coordinates avoid double-counting native scroll-padding in Free mode.
        this.scrollOwner.scrollBy({top:this.items[index].getBoundingClientRect().top - viewportTop - headerInset, behavior});
      } else if (animate) this.items[index].scrollIntoView({block:'start',behavior});
      const announcement=this.querySelector('.ag-announcement');
      if(animate&&announcement)announcement.textContent=this.items[index].getAttribute('aria-label');
      const thumbnail=this.querySelector('.ag-thumbnail[aria-current=true]');
      if(thumbnail) {
        const rail=thumbnail.parentElement, a=thumbnail.getBoundingClientRect(), b=rail.getBoundingClientRect();
        if(a.bottom>b.bottom)rail.scrollTop+=a.bottom-b.bottom;
        if(a.top<b.top)rail.scrollTop-=b.top-a.top;
        if(a.right>b.right)rail.scrollLeft+=a.right-b.right;
        if(a.left<b.left)rail.scrollLeft-=b.left-a.left;
      }
    }
    handleClick(event) {
      const target=event.target.closest('button,a');
      if(!target||!this.contains(target))return;
      if(target.hasAttribute('data-go')){event.preventDefault();this.go(Number(target.dataset.go));}
      else if(target.hasAttribute('data-next'))this.go(this.active+1);
      else if(target.hasAttribute('data-previous'))this.go(this.active-1);
      else if(target.hasAttribute('data-zoom')){if(!this.zoom?.showModal)return;event.preventDefault();this.openZoom(target);}
      else if(target.hasAttribute('data-zoom-close'))window.AplovioLayers ? window.AplovioLayers.close(this.zoom, 'button') : this.zoom.close();
      else if(target.hasAttribute('data-zoom-toggle')){const on=!this.zoom.hasAttribute('data-magnified');this.zoom.toggleAttribute('data-magnified',on);target.setAttribute('aria-pressed',on);}
      else if(target.hasAttribute('data-play'))this.play(target);
      else if(target.hasAttribute('data-information-link')) {
        const info=document.getElementById(this.dataset.information);
        if(info){event.preventDefault();info.scrollIntoView({block:'start',behavior:this.motion.matches?'instant':'smooth'});info.focus({preventScroll:true});}
      }
    }
    handleKey(event) {
      if(this.zoom?.open) return;
      if(!this.horizontal||!this.contains(event.target)||event.target.closest('input,select,textarea,video,model-viewer'))return;
      if(!event.target.closest('.ag-track,.ag-thumbnails,.ag-pagination'))return;
      const rtl=getComputedStyle(this.track).direction==='rtl';
      if(event.key==='ArrowRight'){event.preventDefault();this.go(this.active+(rtl?-1:1));}
      else if(event.key==='ArrowLeft'){event.preventDefault();this.go(this.active+(rtl?1:-1));}
      else if(event.key==='Home'){event.preventDefault();this.go(0);}
      else if(event.key==='End'){event.preventDefault();this.go(this.items.length-1);}
    }
    openZoom(link) {
      this.zoomOrigin=link;
      const image=new Image();image.alt=link.querySelector('img')?.alt||'';image.src=link.href;image.decoding='async';
      this.zoom.querySelector('.ag-zoom-scroll').replaceChildren(image);
      this.zoom.removeAttribute('data-magnified');
      this.zoom.querySelector('[data-zoom-toggle]').setAttribute('aria-pressed','false');
      if (window.AplovioLayers) window.AplovioLayers.open(this.zoom, link);
      else this.zoom.showModal();
      this.zoom.querySelector('[data-zoom-close]').focus();
    }
    finishZoom() {
      if(this.previousOverflow!==undefined){document.documentElement.style.overflow=this.previousOverflow;this.previousOverflow=undefined;}
      this.zoom?.querySelector('.ag-zoom-scroll')?.replaceChildren();
      if(this.zoomOrigin?.isConnected)this.zoomOrigin.focus({preventScroll:true});
      this.zoomOrigin=null;
    }
    play(button) {
      const item=button.closest('.ag-item'), template=item.querySelector('[data-player-template]');
      if(!template)return;
      this.go(this.items.indexOf(item),false);
      this.stopPlayers();
      const player=document.createElement('div');player.className='ag-rich-player';
      player.append(template.content.cloneNode(true));item.append(player);button.hidden=true;
      this.playObserver?.disconnect();
      this.playObserver=new IntersectionObserver(entries=>{if(entries.some(entry=>!entry.isIntersecting))this.stopPlayers();},{threshold:0});
      this.playObserver.observe(item);
      const video=player.querySelector('video');
      if(video){video.focus();video.play().catch(()=>{});}
      const frame=player.querySelector('iframe');if(frame)frame.focus();
      const model=player.querySelector('model-viewer');
      if(model&&window.Shopify?.loadFeatures) {
        if(!document.querySelector('[data-aplovio-model-style]')){const style=document.createElement('link');style.rel='stylesheet';style.href='https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';style.dataset.aplovioModelStyle='';document.head.append(style);}
        window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:error=>{if(!error&&model.isConnected&&window.Shopify.ModelViewerUI)player.modelUI=new window.Shopify.ModelViewerUI(model);}}]);
      }
    }
    stopPlayers(except) {
      this.items?.forEach(item=>{if(item===except)return;const player=item.querySelector('.ag-rich-player');if(player){player.querySelector('video')?.pause();player.modelUI?.destroy?.();player.remove();this.playObserver?.disconnect();const poster=item.querySelector('[data-play]');if(poster)poster.hidden=false;}});
    }
  }
  customElements.define('aplovio-gallery',AplovioGallery);
}
